import React from 'react';
import { useOutletContext } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { CustomDatePicker } from '../../common';

const RegisterStep1 = () => {
    const {
        formData,
        handleInputChange,
        errors,
        handleNextStep,
        setFormData,
        setErrors,
        autocompletePaperSx,
        GENDER_OPTIONS
    } = useOutletContext();


    return (
        <>
            {/* Full Name */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
                    <Typography component="span" sx={{ color: '#64748b', fontSize: '12px', fontFamily: "'Poppins', sans-serif" }}>
                        {`${50 - (formData.fullName || '').length} characters remaining`}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1.5px solid',
                        borderColor: errors.fullName ? '#e74c3c' : '#cbd5e1',
                        borderRadius: '16px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.25s ease',
                        '&:focus-within': {
                            borderColor: '#ff502d',
                            boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                        }
                    }}
                >
                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                    </Box>
                    <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                    <Box
                        component="input"
                        type="text"
                        name="fullName"
                        placeholder="Full Name"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        maxLength={50}
                        onBlur={(e) => {
                            if (setFormData) {
                                setFormData(prev => ({
                                    ...prev,
                                    fullName: e.target.value.trim()
                                }));
                            }
                        }}
                        sx={{
                            flex: 1,
                            padding: '16px',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '15px',
                            color: '#334155',
                            outline: 'none',
                            fontFamily: "'Poppins', sans-serif",
                            width: '100%'
                        }}
                    />
                </Box>
                {errors.fullName && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', px: 1, fontFamily: "'Poppins', sans-serif" }}>
                        {errors.fullName}
                    </Typography>
                )}
            </Box>


            {/* Date of Birth */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1.5px solid',
                        borderColor: errors.dateOfBirth ? '#e74c3c' : '#cbd5e1',
                        borderRadius: '16px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.25s ease',
                        '&:focus-within': {
                            borderColor: '#ff502d',
                            boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                        }
                    }}
                >
                    <CustomDatePicker
                        name="dateOfBirth"
                        placeholder="Select Date of Birth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split('T')[0]}
                    />
                </Box>
                {errors.dateOfBirth && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                        {errors.dateOfBirth}
                    </Typography>
                )}
            </Box>

            {/* Gender */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1.5px solid',
                        borderColor: errors.gender ? '#e74c3c' : '#cbd5e1',
                        borderRadius: '16px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.25s ease',
                        '&:focus-within': {
                            borderColor: '#ff502d',
                            boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                        }
                    }}
                >
                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                        </svg>
                    </Box>
                    <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                    <Autocomplete
                        options={GENDER_OPTIONS}
                        getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
                        value={GENDER_OPTIONS.find(o => o.value === formData.gender) || null}
                        onChange={(_, newVal) => {
                            setFormData(prev => ({
                                ...prev,
                                gender: newVal ? newVal.value : ''
                            }));
                            if (setErrors) {
                                setErrors(prev => ({
                                    ...prev,
                                    gender: ''
                                }));
                            }
                        }}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'transparent',
                                paddingLeft: '8px',
                                '& fieldset': {
                                    border: 'none'
                                },
                                '&:hover fieldset': {
                                    border: 'none'
                                },
                                '&.Mui-focused fieldset': {
                                    border: 'none'
                                },
                                '& input': {
                                    color: '#334155',
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '15px',
                                    padding: '16px 14px 16px 4px'
                                }
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select Gender"
                            />
                        )}
                        slotProps={{
                            paper: {
                                sx: autocompletePaperSx
                            }
                        }}
                    />
                </Box>
                {errors.gender && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                        {errors.gender}
                    </Typography>
                )}
            </Box>

            {/* Next Step Button */}
            <Box sx={{ display: 'flex', mt: '10px', width: '100%' }}>
                <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={
                        !(formData.fullName || '').trim() ||
                        !formData.dateOfBirth ||
                        !formData.gender
                    }
                    variant="contained"
                    sx={{
                        width: '100%',
                        background: '#ff502d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        py: 1.2,
                        px: 3,
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textTransform: 'none',
                        boxShadow: '0 3px 8px rgba(255, 80, 45, 0.15)',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.25s ease',
                        '&:hover:not(:disabled)': {
                            background: '#e04324',
                            transform: 'translateY(-1.5px)',
                            boxShadow: '0 5px 12px rgba(255, 80, 45, 0.25)'
                        },
                        '&:disabled': {
                            opacity: 0.6,
                            backgroundColor: '#ff502d',
                            color: '#ffffff',
                            boxShadow: 'none',
                            cursor: 'not-allowed'
                        }
                    }}
                >
                    Next Step
                </Button>
            </Box>
        </>
    );
};

export default RegisterStep1;
